import React from 'react';
import { Layout, Typography, Avatar, Dropdown, Menu } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { resetAnalytics } from '../../lib/analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import sensorflowLogo from '../../assets/sensorflow-logo.svg';
import { LabelingGuideButton } from '../LabelingGuide';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const { Header } = Layout;

const AppHeader: React.FC = () => {
  const { user, logout } = useAuth0();
  const { isAdmin } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();

  const currentTab = location.pathname.startsWith('/worklist')
    ? 'worklist'
    : location.pathname.startsWith('/tasks')
    ? 'tasks'
    : location.pathname.startsWith('/actions')
    ? 'actions'
    : location.pathname.startsWith('/usage')
    ? 'usage'
    : location.pathname.startsWith('/agent-health')
    ? 'agent-health'
    : location.pathname.startsWith('/help')
    ? 'help'
    : location.pathname.startsWith('/issues')
    ? 'issues'
    : 'triage';

  const routeByKey: Record<string, string> = {
    worklist: '/worklist',
    triage: '/triage',
    issues: '/issues',
    tasks: '/tasks',
    actions: '/actions',
    usage: '/usage',
    'agent-health': '/agent-health',
    help: '/help',
  };

  // UX-054: show who the operator is signed in as before the Logout row.
  // antd Dropdown.Menu's `type:'group'` renders a non-clickable label header.
  const userDisplayName = user?.name || user?.email || 'Signed in';
  const userEmail = user?.email && user.email !== user?.name ? user.email : null;
  const menuItems = [
    {
      key: 'identity',
      type: 'group' as const,
      label: (
        <div style={{ lineHeight: 1.3, padding: '2px 0' }}>
          <div style={{ fontWeight: 500, color: '#262626' }}>{userDisplayName}</div>
          {userEmail && (
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{userEmail}</div>
          )}
        </div>
      ),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        resetAnalytics();
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
    },
  ];

  return (
    <Header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        borderBottom: '1px solid #e8e8e8',
        height: 48,
        lineHeight: '48px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 24, flexShrink: 0 }}>
          <img src={sensorflowLogo} alt="SensorFlow" style={{ height: 20 }} />
          <Typography.Text strong style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            Fleet Triage
          </Typography.Text>
        </div>
        <Menu
          mode="horizontal"
          selectedKeys={[currentTab]}
          // Client-side nav — previously this was window.location.href which
          // full-reloaded the SPA on every tab click. Preserve the search
          // string so switching between /triage and /issues (and back to
          // /tasks etc.) keeps the operator's `loc`/`status`/`class` scope.
          // The /tasks route uses `:locationId` instead of `?loc=` so we
          // don't carry the search there.
          onClick={({ key }) => {
            const target = routeByKey[key] || '/triage';
            const preserveSearch = target === '/triage' || target === '/issues';
            navigate({
              pathname: target,
              search: preserveSearch ? window.location.search : '',
            });
          }}
          items={[
            { key: 'worklist', label: 'Worklist' },
            { key: 'triage', label: 'Triage' },
            { key: 'issues', label: 'Issues' },
            { key: 'tasks', label: 'Work Tasks' },
            { key: 'actions', label: 'Actions' },
            // Agent Health hidden for non-admin during the agent-testing
            // phase. The route itself redirects non-admins to /triage, but
            // hiding the tab avoids a dead-tab tease in the navbar.
            ...(isAdmin ? [{ key: 'usage', label: 'Usage' }, { key: 'agent-health', label: 'Agent Health' }] : []),
            { key: 'help', label: 'Help' },
          ]}
          style={{ border: 'none', lineHeight: '46px', minWidth: 0, flex: 1 }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Always-available labeling guide — primary call-to-action for ops
            to actually start writing operator labels (current count: 0). */}
        <LabelingGuideButton size="small" label="Labeling guide" />
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Avatar
            style={{ backgroundColor: '#006EB7', cursor: 'pointer', fontSize: 12 }}
            size="small"
          >
            {user?.name?.charAt(0) || '?'}
          </Avatar>
        </Dropdown>
      </div>
    </Header>
  );
};

const TriageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <AppHeader />
      <Layout style={{ flex: 1, overflow: 'hidden' }}>
        {children}
      </Layout>
    </Layout>
  );
};

export default TriageLayout;
